import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Robot, CheckCircle, Sparkle, Star, ArrowRight } from "@phosphor-icons/react";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    monthly: 49,
    annual: 490,
    blurb: "For solo owners and tiny teams.",
    features: [
      "Up to 500 emails / mo",
      "1,000 leads / mo",
      "Customer chat bot widget",
      "Email triage + draft replies",
      "1 user seat",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    monthly: 199,
    annual: 1990,
    blurb: "For growing teams that need volume.",
    features: [
      "Up to 5,000 emails / mo",
      "10,000 leads / mo",
      "All Starter features",
      "Lead routing rules",
      "Slack + Gmail integrations",
      "5 user seats",
    ],
    highlight: true,
  },
  {
    id: "pro",
    name: "Pro · Agency",
    monthly: 500,
    annual: 5000,
    blurb: "Custom workflows, white-glove setup.",
    features: [
      "Unlimited volume",
      "Custom automations",
      "Priority support · SLA",
      "Multi-workspace",
      "Unlimited seats",
    ],
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [success, setSuccess] = useState(null);

  const checkout = async (plan) => {
    if (!user) return nav("/register");
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, billing });
      setSuccess(data);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white">
      <header className="border-b border-[var(--line)] sticky top-0 bg-white/90 backdrop-blur z-40">
        <div className="max-w-[1320px] mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[var(--ink)] flex items-center justify-center">
              <Robot size={22} weight="duotone" color="#FFD600" />
            </div>
            <span className="font-display text-2xl tracking-tight">Robocope</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/" className="hover:underline">Home</Link>
            {user ? (
              <Link to="/dashboard" className="btn-primary text-sm" data-testid="dashboard-link">Dashboard <ArrowRight size={16} weight="bold" /></Link>
            ) : (
              <Link to="/register" className="btn-primary text-sm">Get started <ArrowRight size={16} weight="bold" /></Link>
            )}
          </nav>
        </div>
      </header>

      <section className="py-20">
        <div className="max-w-[1320px] mx-auto px-8">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-7">
              <div className="overline text-[var(--ink-soft)]">/ Pricing</div>
              <h1 className="text-5xl lg:text-6xl font-display tracking-tight mt-3">
                One AI employee. <span className="bg-[var(--accent)] px-2 inline-block -rotate-1">Three salary tiers.</span>
              </h1>
              <p className="text-[var(--ink-soft)] text-lg mt-6 max-w-xl">
                Pick the volume that matches your business. Upgrade or downgrade any time. Cancel any time.
              </p>
            </div>
            <div className="lg:col-span-5 flex justify-end">
              <div className="inline-flex border border-[var(--ink)]" data-testid="billing-toggle">
                <button
                  onClick={() => setBilling("monthly")}
                  className={`px-4 py-2 text-sm font-medium ${billing === "monthly" ? "bg-[var(--ink)] text-white" : ""}`}
                  data-testid="billing-monthly"
                >Monthly</button>
                <button
                  onClick={() => setBilling("annual")}
                  className={`px-4 py-2 text-sm font-medium ${billing === "annual" ? "bg-[var(--ink)] text-white" : ""}`}
                  data-testid="billing-annual"
                >Annual <span className="text-[var(--accent)] ml-1">-17%</span></button>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className="p-8 flex flex-col"
                style={{
                  background: p.highlight ? "#0A0A0A" : "#FFFFFF",
                  color: p.highlight ? "#FFFFFF" : "#0A0A0A",
                  border: "1px solid #0A0A0A",
                  borderRadius: "2px",
                }}
                data-testid={`plan-${p.id}`}
              >
                {p.highlight && (
                  <div
                    className="self-start mb-4 px-2 py-1 text-xs font-mono-rc tracking-wider"
                    style={{ background: "#FFD600", color: "#0A0A0A", border: "1px solid #0A0A0A" }}
                  >
                    MOST POPULAR
                  </div>
                )}
                <div className="font-display text-3xl" style={{ color: p.highlight ? "#FFFFFF" : "#0A0A0A" }}>
                  {p.name}
                </div>
                <div
                  className="text-sm mt-2"
                  style={{ color: p.highlight ? "rgba(255,255,255,0.65)" : "#4B5563" }}
                >
                  {p.blurb}
                </div>
                <div className="mt-6 flex items-baseline gap-2">
                  <span
                    className="font-display text-6xl tracking-tighter"
                    style={{ color: p.highlight ? "#FFFFFF" : "#0A0A0A" }}
                  >
                    ${billing === "monthly" ? p.monthly : Math.round(p.annual / 12)}
                  </span>
                  <span
                    className="text-sm"
                    style={{ color: p.highlight ? "rgba(255,255,255,0.55)" : "#4B5563" }}
                  >
                    / mo
                  </span>
                </div>
                {billing === "annual" && (
                  <div
                    className="text-xs font-mono-rc"
                    style={{ color: p.highlight ? "rgba(255,255,255,0.55)" : "#4B5563" }}
                  >
                    billed ${p.annual}/yr
                  </div>
                )}
                <ul className="mt-8 space-y-2 flex-1">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-2 text-sm"
                      style={{ color: p.highlight ? "#FFFFFF" : "#0A0A0A" }}
                    >
                      <CheckCircle
                        size={18}
                        weight="fill"
                        className="mt-0.5"
                        color={p.highlight ? "#FFD600" : "#0047FF"}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => checkout(p.id)}
                  disabled={busy === p.id}
                  className={`mt-8 ${p.highlight ? "btn-accent" : "btn-primary"} w-full justify-center`}
                  data-testid={`checkout-${p.id}`}
                >
                  {busy === p.id ? "Processing…" : user ? "Choose plan" : "Start trial"} <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 cell p-4 text-xs text-[var(--ink-soft)] bg-[var(--bg-soft)] flex items-center gap-2">
            <Sparkle weight="fill" className="text-[var(--brand)]" />
            <strong>Demo mode:</strong> checkout is mocked for V1. No real payment. Stripe integration ships next.
          </div>
        </div>
      </section>

      {success && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSuccess(null)}>
          <div onClick={(e) => e.stopPropagation()} className="cell-strong bg-white p-8 max-w-md w-full text-center" data-testid="checkout-success">
            <div className="w-16 h-16 bg-[var(--accent)] mx-auto flex items-center justify-center"><CheckCircle size={36} weight="fill" /></div>
            <div className="font-display text-3xl mt-4">You're hired.</div>
            <div className="text-[var(--ink-soft)] mt-2">Mock subscription confirmed: <strong className="text-[var(--ink)]">{success.plan.toUpperCase()}</strong> · ${success.amount} · order {success.order_id}</div>
            <Link to="/dashboard" className="btn-primary mt-6 inline-flex" onClick={() => setSuccess(null)} data-testid="success-go-dashboard">
              Open dashboard <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
